import React, { useEffect, useState, useCallback } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'

import { stationsActions } from '../../core/stations'
import {
  brandsActions,
  Brand,
  getDefaultBrandCodeByRole,
} from '../../core/brands'
import { modalActions } from '../../core/modal'
import RootState from '../../core/root-state'
import Layout from '../layouts/layout'
import Wrapper from '../layouts/wrapper'
import StationItem from '../components/stations/station-item'
import Button from '../components/button'
import { Station } from '../../core/stations'
import { strings } from '../components/stations/stations-content'
import styles from '../styles/modules/stations/stations.module.scss'
import { BrandFilter, BrandCode } from '../components/common'
import {
  roleType,
  Permission,
  hasPermission,
  isSbjUser,
} from '../../core/auth0'
import withPermissionCheck from '../../core/access-control/with-permission-check'

interface Props {
  stations: Station[]
  isLoading: boolean
  role: roleType
  brands: Brand[]
  currentBrandCode: string | null
  routerAction: string
  fetchStations: (brandCode?: string | null) => void
  fetchStationsEvery: () => void
  fetchStationsEveryFinished: () => void
  setCurrentBrandCode: (brandCode: string | null) => void
  showCreateStation: () => void
  bootMachine: (stationId: number) => void
  shutMachine: (stationId: number) => void
  fetchBrands: () => void
}

const StationsScreen: React.FC<Props> = ({
  stations,
  isLoading,
  role,
  brands,
  currentBrandCode,
  routerAction,
  fetchStations,
  fetchStationsEvery,
  fetchStationsEveryFinished,
  setCurrentBrandCode,
  showCreateStation,
  bootMachine,
  shutMachine,
  fetchBrands,
}) => {
  // ロールに基づいてデフォルトブランドを決定
  const defaultBrandCode = getDefaultBrandCodeByRole(isSbjUser(role))
  const shouldRestoreBrand = routerAction === 'POP'
  const [selectedBrand, setSelectedBrand] = useState<BrandCode>(
    shouldRestoreBrand ? currentBrandCode ?? defaultBrandCode : defaultBrandCode
  )
  const [activeAction, setActiveAction] = useState<'power' | 'filter' | null>(
    null
  )
  const [activeStationId, setActiveStationId] = useState<number | null>(null)
  const isAdmin = role === roleType.ADMIN
  const canCreateStation = hasPermission(role, Permission.STATION_CREATE)
  const canControlStation = hasPermission(role, Permission.STATION_POWER)

  // 継続取得処理をuseCallbackでラップ
  const startPolling = useCallback(() => {
    fetchStationsEvery()
  }, [fetchStationsEvery])

  const stopPolling = useCallback(() => {
    fetchStationsEveryFinished()
  }, [fetchStationsEveryFinished])

  useEffect(() => {
    // ページ遷移時にブランドフィルタを初期化
    const brandCodeToUse = shouldRestoreBrand
      ? currentBrandCode ?? defaultBrandCode
      : defaultBrandCode
    setSelectedBrand(brandCodeToUse)
    setCurrentBrandCode(brandCodeToUse)
    fetchStations(brandCodeToUse)
    fetchBrands()
    // 初期化処理のため、コンポーネントマウント時に一度だけ実行される必要がある
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    startPolling()
    return () => {
      stopPolling()
    }
  }, [startPolling, stopPolling])

  const handleBrandChange = (brand: BrandCode) => {
    setSelectedBrand(brand)
  }

  const handleFilterClick = () => {
    setActiveAction('filter')
    setCurrentBrandCode(selectedBrand)
    fetchStations(selectedBrand)
  }

  useEffect(() => {
    if (activeAction === 'filter' && !isLoading) {
      setActiveAction(null)
    }
  }, [activeAction, isLoading])

  const handleOnClickPower = (station: Station) => {
    const response = window.confirm(
      station.machine.power === 'on'
        ? strings.cautionStationStop
        : strings.cautionStationStart
    )
    if (response) {
      setActiveAction('power')
      setActiveStationId(station.id)

      if (
        station.machine.power === 'on' ||
        station.machine.power === 'should_be_on'
      ) {
        shutMachine(station.id)
      } else {
        bootMachine(station.id)
      }
    }
  }

  const handleOnClickChangeStatus = (station: Station) => {
    const response = window.confirm(
      station.machine.status === 'station_stop'
        ? strings.cautionStationStart
        : strings.cautionStationStop
    )
    if (response) {
      setActiveAction('power')
      setActiveStationId(station.id)

      if (station.machine.status === 'station_stop') {
        bootMachine(station.id)
      } else {
        shutMachine(station.id)
      }
    }
  }
  return (
    <Layout location={'stations'}>
      <Wrapper title={strings.title}>
        <div className={styles.filterContainer}>
          <BrandFilter
            currentBrand={selectedBrand}
            onBrandChange={handleBrandChange}
            onFilterClick={handleFilterClick}
            isAdminOnly={isAdmin}
            brands={brands}
            isLoading={isLoading && activeAction === 'filter'}
          />
        </div>
        <div>
          稼働中：新規オーダー受付可/調理可能/サイネージ「オーダー受付中」
        </div>
        <div>
          停止中：新規オーダー受付不可/調理停止/サイネージ「営業時間外」
        </div>
        <div className={styles.wrapper}>
          {canCreateStation && (
            <Button
              title={strings.createStationButton}
              onClick={() => {
                showCreateStation()
              }}
              className={styles.stationCreateButton}
            />
          )}
          <div className={styles.stationContainer}>
            <h3>開店</h3>
            <div className={styles.stationGroup}>
              {stations
                .filter(station => station.machine && !station.isClosed)
                .map(station => (
                  <StationItem
                    station={station}
                    key={station.id}
                    onClickPower={() => handleOnClickPower(station)}
                    powerIsLoading={
                      isLoading &&
                      activeAction === 'power' &&
                      activeStationId === station.id
                    }
                    onClickChangeStatus={() =>
                      handleOnClickChangeStatus(station)
                    }
                    changeStatusIsLoading={
                      isLoading &&
                      activeAction === 'power' &&
                      activeStationId === station.id
                    }
                    canControlStation={canControlStation}
                  />
                ))}
            </div>
          </div>
          <div className={styles.closedStationContainer}>
            <h3>閉店/マシン未紐付</h3>
            <div className={styles.closedStationGroup}>
              {stations
                .filter(station => !station.machine || station.isClosed)
                .map(station => (
                  <StationItem
                    station={station}
                    key={station.id}
                    onClickPower={() => handleOnClickPower(station)}
                    powerIsLoading={
                      isLoading &&
                      activeAction === 'power' &&
                      activeStationId === station.id
                    }
                    onClickChangeStatus={() => handleOnClickPower(station)}
                    changeStatusIsLoading={
                      isLoading &&
                      activeAction === 'power' &&
                      activeStationId === station.id
                    }
                    canControlStation={canControlStation}
                  />
                ))}
            </div>
          </div>
        </div>
      </Wrapper>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => ({
  stations: state.stations.stations,
  isLoading: state.stations.isLoading,
  role: state.auth0.role,
  brands: state.brands.brands,
  currentBrandCode: state.stations.currentBrandCode,
  routerAction: state.router.action,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchStations: (brandCode?: string | null) =>
    dispatch(stationsActions.fetchStations(brandCode)),
  fetchStationsEvery: () => dispatch(stationsActions.fetchStationsEvery()),
  fetchStationsEveryFinished: () =>
    dispatch(stationsActions.fetchStationsEveryFinished()),
  setCurrentBrandCode: (brandCode: string | null) =>
    dispatch(stationsActions.setCurrentBrandCode(brandCode)),
  showCreateStation: () => dispatch(modalActions.showCreateStation()),
  bootMachine: (stationId: number) =>
    dispatch(stationsActions.bootMachine(stationId)),
  shutMachine: (stationId: number) =>
    dispatch(stationsActions.shutMachine(stationId)),
  fetchBrands: () => dispatch(brandsActions.fetchBrands()),
})

const ConnectedStationsScreen = connect(
  mapStateToProps,
  mapDispatchToProps
)(StationsScreen)

export default withPermissionCheck(ConnectedStationsScreen, {
  requiredPermission: Permission.STATION_READ,
  redirectTo: '/404',
})
